import React, { useState, useEffect, useRef } from 'react';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import ScheduleTable from './ScheduleTable';
import FixedHelpButton from './FixedHelpButton';

const MainLayout = ({
  courses,
  schedule,
  weekdays,
  timeSlots,
  backgroundImage,
  backgroundOpacity,
  backgroundBlur,
  backgroundMode,
  backgroundColor,
  showPeriodColumn,
  onBackgroundImageChange,
  onBackgroundOpacityChange,
  onBackgroundBlurChange,
  onShowBackgroundSettings,
  onTogglePeriodColumn,
  onAddCourse,
  onRemoveCourse,
  onShowCourseDetails,
  onShowBatchModal,
  onAddTime,
  onAddDay,
  onDeleteTime,
  onDeleteDay,
  onUpdateTime,
  onShowTimeManagement,
  onDrop,
  onManualInput,
  onDragUpdate,
  onAddNewCourse,
  onSave,
  onLoad,
  onExportImage,
  onPrint,
  onImport,
  onExport,
  onClearData,
  showHelpModal,
  setShowHelpModal
}) => {
  const [shouldSticky, setShouldSticky] = useState(true);
  const scheduleContainerRef = useRef(null);

  // 检查课程表内容是否超出屏幕高度
  const checkContentHeight = () => {
    if (scheduleContainerRef.current) {
      const container = scheduleContainerRef.current;
      // 只检测课程表表格的高度，独立于其他模块
      const scheduleTable = container.querySelector('#scheduleTable');
      if (scheduleTable) {
        const tableHeight = scheduleTable.offsetHeight;
        // 计算课程表容器的可用高度（减去标题和padding）
        const titleHeight = container.querySelector('h1')?.offsetHeight || 0;
        const containerPadding = 40; // p-5 = 20px * 2
        const availableHeight = window.innerHeight - 200 - titleHeight - containerPadding;
        
        // 如果表格高度小于可用高度，使用sticky定位
        // 如果表格高度大于可用高度，不使用sticky定位，允许滚动
        const needsSticky = tableHeight < availableHeight;
        setShouldSticky(needsSticky);
        
        // 强制设置容器高度，确保独立性
        if (needsSticky) {
          container.style.height = 'fit-content';
          container.style.maxHeight = 'none';
        } else {
          container.style.height = 'auto';
          container.style.maxHeight = 'none';
        }
      }
    }
  };

  // 监听窗口大小变化和内容变化
  useEffect(() => {
    checkContentHeight();
    
    const handleResize = () => {
      checkContentHeight();
    };

    window.addEventListener('resize', handleResize);
    
    // 使用MutationObserver监听内容变化
    const observer = new MutationObserver(() => {
      setTimeout(checkContentHeight, 100); // 延迟检查，确保DOM更新完成
    });

    if (scheduleContainerRef.current) {
      observer.observe(scheduleContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [schedule, weekdays, timeSlots]); // 只依赖课程表相关的状态变化
  return (
    <div className="flex w-full gap-3 p-3">
      <LeftSidebar
        courses={courses}
        onAddCourse={onAddCourse}
        onRemoveCourse={onRemoveCourse}
        onShowCourseDetails={onShowCourseDetails}
        onShowBatchModal={onShowBatchModal}
      />
      
      <div 
        ref={scheduleContainerRef}
        className={`flex-1 bg-white rounded-xl p-5 shadow-lg border border-gray-200 min-w-0 ${
          shouldSticky ? 'sticky top-5' : ''
        }`}
      >
        <ScheduleTable
          courses={courses}
          schedule={schedule}
          weekdays={weekdays}
          timeSlots={timeSlots}
          onDrop={onDrop}
          onManualInput={onManualInput}
          onDragUpdate={onDragUpdate}
          onAddNewCourse={onAddNewCourse}
          onDeleteTime={onDeleteTime}
          onDeleteDay={onDeleteDay}
          onUpdateTime={onUpdateTime}
          backgroundImage={backgroundImage}
          backgroundOpacity={backgroundOpacity}
          backgroundBlur={backgroundBlur}
          backgroundMode={backgroundMode}
          backgroundColor={backgroundColor}
          showPeriodColumn={showPeriodColumn}
        />
      </div>
      
      <RightSidebar
        onShowBackgroundSettings={onShowBackgroundSettings}
        onShowTimeManagement={onShowTimeManagement}
        onAddDay={onAddDay}
        onSave={onSave}
        onLoad={onLoad}
        onExportImage={onExportImage}
        onPrint={onPrint}
        onImport={onImport}
        onExport={onExport}
        onClearData={onClearData}
        showPeriodColumn={showPeriodColumn}
        onTogglePeriodColumn={onTogglePeriodColumn}
      />
      
      <FixedHelpButton onShowHelp={() => setShowHelpModal(true)} />
    </div>
  );
};

export default MainLayout;
